import type { CeType, Volume } from '@wix/adi-content-api';
import type {
  ColorPalette,
  CompRef,
  CompStructure,
  SerializedCompStructure,
  StyleRef,
  TextThemeMap,
} from '@wix/document-services-types';
import type {
  PageSectionContent,
  PresetContentCategory,
  UserData,
} from '@wix/editor-content-provider';
import type {
  ColorName,
  KitDefinition,
  LinkedColorValue,
} from '@wix/editor-kits';
import { HttpClient } from '@wix/http-client';
import { ChatControllerBiEventNumbers } from './chatController/constants';
import { type SiteGeneratorBiEventNumbers } from './consts';

export type PagePartKeys = 'header' | 'sections' | 'footer';

export interface PresetStructure {
  title: string;
  id: string;
  structure: SerializedCompStructure;
  mobileStructure: SerializedCompStructure;
}

export type UploadFileFn = (
  externalFile: {
    name: string;
    url: string;
  },
  tempMetaSiteInstance: string,
) => Promise<string>;

export interface SiteGeneratorOptions {
  contentLanguageName?: string;
  languageCode?: string;
  presetsBundleBaseUrl: string;
  staticMediaUrl: string;
  isStagingMode?: boolean;
  interactionStarted?: (interactionName: SiteGeneratorInteractionName) => void;
  interactionEnded?: (interactionName: SiteGeneratorInteractionName) => void;
  isDebugMode?: boolean;
  userData: UserData;
  shouldUseSgInstallOrigin: boolean;
  translate: (key: string) => string;
  contentVersion?: string;
  isAiImageInjectionOpen?: boolean;
  siteCommonBi?: CommonBiFn;
  fileUploadFn?: UploadFileFn;
}

export interface SectionPresetStructure
  extends PresetStructure,
    PageSectionContent {}

export interface HeaderPresetStructure extends PresetStructure {
  ceType: undefined;
  contentCategory: undefined;
  sourceTemplateId: string | undefined;
  logoAlignment: string;
  mobileLogoAlignment: string;
}

export interface FooterPresetStructure extends PresetStructure {
  ceType: undefined;
  contentCategory: undefined;
  sourceTemplateId: string | undefined;
  logoAlignment: string;
  mobileLogoAlignment: string;
}

export interface PageStructure {
  sections: SectionPresetStructure[];
}

export interface FullHomepageStructure extends PageStructure {
  header: HeaderPresetStructure | null;
  footer: FooterPresetStructure | null;
}

export interface GeneratedPage extends PageStructure {
  name: string;
}

export interface GeneratedHomepage extends FullHomepageStructure {
  name: string;
}

export interface LayoutFamilyDefinition {
  title: string;
  _id?: string;
  volume?: Volume;
  descriptionKey: string;
  nameTranslationKey: string;
  msid?: string;
  description: string;
  tags: string[];
  goodFor: string[];
  isPublished: boolean;
  disableShapeDividers: boolean;
  disableGradient: boolean;
  pinElements?: SerializedCompStructure[];
}

export type PresetIdToSiteIdMap = Record<string, string>;

export type HeaderWidgetAlignment = Record<string, { alignment?: string }>;

export interface SiteTheme {
  textThemes: TextThemeMap;
  colors: ColorPalette;
  linkedColors?: Partial<Record<ColorName, LinkedColorValue>>;
  styles: Record<string, StyleRef>;
}

export interface CompIdToParentIdMap {
  [compId: string]: string;
}

// TODO: use real types from this package
export interface PresetDefinition {
  _id: string;
  appIds: string[];
  layoutFamily: string;
  msid: string;
  title: string;
  presetJsonUrl: string;
  presetMobileJsonUrl: string;
  styleRules?: string[];
}

export interface SectionPresetDefinition extends PresetDefinition {
  contentCategory: PresetContentCategory;
}

export interface HeaderPresetDefinition extends PresetDefinition {
  widgets: HeaderWidgetAlignment;
  logoAlignment: string;
  mobileLogoAlignment: string;
  hasLogo: boolean;
}

export interface FooterPresetDefinition extends PresetDefinition {
  appIds: string[];
  hasLogo: boolean;
  logoAlignment: string;
  mobileLogoAlignment: string;
}

export type SiteGeneratorInteractionName =
  | 'sg_init_content'
  | 'sg_get_headers'
  | 'sg_get_footers'
  | 'sg_build_full_homepage_structure'
  | 'sg_build_page_structure'
  | 'sg_inject_site_structure_content'
  | 'sg_add_as_homepage'
  | 'sg_inject_kit'
  | 'sg_add_page'
  | 'sg_set_chosen_apps'
  | 'sg_verify_widgets_installed'
  | 'sg_content_provider_get_content'
  | 'sg_content_injector_inject_preset_structure_full_content'
  | 'sg_content_injector_inject_preset_structure_only_media'
  | 'sg_ai_inject_content'
  | 'sg_ds_ai_inject_content'
  | 'sg_run_post_generation'
  | 'sg_adjust_mobile'
  | 'sg_get_color_from_logo'
  | 'sg_ai_image_injection_bundle'
  | 'sg_ai_image_injection'
  | 'sg_ai_image_injection_upload_bundle'
  | 'sg_ai_image_injection_upload_single'
  | 'sg_generate_image_prompts'
  //   Staging
  | 'sg_init_content_staging'
  | 'sg_adjust_mobile_staging'
  | 'sg_get_headers_staging'
  | 'sg_get_footers_staging'
  | 'sg_build_full_homepage_structure_staging'
  | 'sg_build_page_structure_staging'
  | 'sg_inject_site_structure_content_staging'
  | 'sg_add_as_homepage_staging'
  | 'sg_add_page_staging'
  | 'sg_set_chosen_apps_staging'
  | 'sg_verify_widgets_installed_staging'
  | 'sg_run_post_generation_staging'
  | 'sg_predict_kits';

export type FedopsInteraction = (
  interactionName: SiteGeneratorInteractionName,
  options?: {
    customParams?: { [key: string]: string };
  },
) => void;

export interface BuildPageOptions {
  injectCaasText?: boolean;
  injectAiText?: boolean;
}

export type AddedPageWithRef = {
  pageRef: CompRef;
  page: GeneratedPage | GeneratedHomepage;
};

export interface ExtractedStructures {
  structure: SerializedCompStructure[];
  mobileStructure: SerializedCompStructure[];
}

export type Dimensions = { width: number; height: number };

export interface HomepagePresets {
  headerPreset: HeaderPresetDefinition | null;
  sectionsPresets: SectionPresetDefinition[];
  footerPreset: FooterPresetDefinition | null;
}

export type LockedPresetIds = {
  header: string | undefined;
  sections: string[];
  footer: string | undefined;
};

interface PredictKitBaseParams {
  theme: string;
  businessTerm: string;
  businessDescription: string;
  kitDescriptions: string;
}

export interface PredictKitPromptHub {
  client: HttpClient;
  params: PredictKitBaseParams;
  sendBi?: SendBi;
}

export interface PredictKitParams {
  params: Pick<PredictKitBaseParams, 'businessTerm' | 'businessDescription'>;
  withRetry?: boolean;
}

export type GetRegenerateKit =
  | (() => Promise<KitDefinition>)
  | ((currentKit: KitDefinition, usedKits: KitDefinition[]) => KitDefinition);

export interface IKitToUseService {
  getInitialKit(): KitDefinition;

  getRegenerateKit: GetRegenerateKit;
  init: (KitDefinitions: KitDefinition[]) => void;
  theme?: string;
}

export type SendBi = (
  evid: ChatControllerBiEventNumbers,
  fields: Record<string, unknown>,
) => void;

export type BiEventNumbers =
  | SiteGeneratorBiEventNumbers
  | ChatControllerBiEventNumbers;

export type CommonBiFn = (
  evid: BiEventNumbers,
  fields: NonNullable<unknown>,
) => void;

export enum ExtendedContentTypes {
  Header = 'Header',
  Footer = 'Footer',
}

export type ContentTypes = CeType | ExtendedContentTypes;

export type FedopsOptions = (data?: { [key: string]: string }) => {
  customParams: {
    origin: string;
  };
};

export interface ComponentExtractor {
  matches: (compStructure: CompStructure) => boolean;
  extract: (compStructure: CompStructure) => [string, string];
}
